/**
 * Module 6C.3 — Onboarding state helper.
 *
 * Single source of truth for reading and mutating the per-user
 * onboarding stamps. The dashboard and Settings page both depend on
 * this; consolidating it here avoids drift between callers.
 *
 * The backend `/api/onboarding/*` endpoints return `{state: {...}}`
 * where `state` has exactly four fields:
 *   tour_dismissed_at, tour_completed_at,
 *   invite_cta_dismissed_at, lead_source_cta_dismissed_at
 *
 * Each is either an ISO timestamp string (set/done) or null (open).
 */
import { api } from "@/lib/api";

export async function fetchOnboardingState() {
  try {
    const { data } = await api.get("/onboarding/state");
    return data.state || {};
  } catch {
    return {};
  }
}

export async function dismissOnboarding(key) {
  // key ∈ { "tour" | "invite_cta" | "lead_source_cta" }. Anything else
  // 422s on the server — caller bug. Return the new state so the UI
  // can update without a re-fetch.
  const { data } = await api.post("/onboarding/dismiss", { key });
  return data.state || {};
}

export async function completeTour() {
  const { data } = await api.post("/onboarding/complete-tour");
  return data.state || {};
}

export async function replayTour() {
  // Clears tour_dismissed_at + tour_completed_at. CTAs untouched.
  const { data } = await api.post("/onboarding/replay-tour");
  return data.state || {};
}

// ───────── Computed predicates (UI gates) ─────────

export function shouldShowTour(state) {
  if (!state) return false;
  return !state.tour_dismissed_at && !state.tour_completed_at;
}

export function shouldShowInviteCta(state, user) {
  if (!state || !user) return false;
  if (state.invite_cta_dismissed_at) return false;
  // Only owners can invite teammates — gating mirrors the team_manage
  // permission check on POST /api/team.
  return user.role === "owner";
}

export function shouldShowLeadSourceCta(state, user) {
  if (!state || !user) return false;
  if (state.lead_source_cta_dismissed_at) return false;
  // First-run setup prompt — the cohort that *initiates* lead-source
  // integrations on a brand-new studio is owner + salesperson. PMs CAN
  // technically hit the import endpoints (they hold `import_use`), but
  // this is a "set up your studio" CTA, not a "do your daily work"
  // affordance. Surfacing it to PMs would confuse role-of-record.
  return user.role === "owner" || user.role === "salesperson";
}
